import React, { useCallback } from 'react';
import { type Href, type RoutePath, type RouteParams, usePathname, useRouter } from 'expo-router';

import type { AddressType } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import { useResetMenuStack } from '../../routing/hooks/useResetMenuStack';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToMenu = (params: Params = {}) => {
  const { backToPath, isInsideModal, shouldBlockOnInFlightDeliveryOrder = true } = params;

  const router = useRouter();
  const pathname = usePathname();
  const resetMenuStack = useResetMenuStack();

  const shouldResetMenu = Boolean(isInsideModal) || pathname.includes('/locations');
  const shouldDismissAll = isInsideModal || !pathname.includes('/locations');

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const resetToMenu = useCallback(
    (menuRoute: MenuRoute) => {
      const { screenName, ...href } = menuRoute;
      const shouldReturnToSelectedMenu =
        typeof backToPath === 'string' && backToPath.endsWith('/menu');
      const returnHref = shouldReturnToSelectedMenu ? href : (backToPath ?? href);

      const didResetMenuStack = resetMenuStack({
        screenName,
        params: href.params,
      });

      if (!didResetMenuStack) {
        router.dismissTo(returnHref);

        return;
      }
      router.dismissTo(returnHref);
    },
    [backToPath, resetMenuStack, router],
  );

  const handleGoToMenu = React.useCallback(
    async (restaurantSlug: string, category?: string) => {
      const canDismiss = shouldDismissAll ? router.canDismiss() : false;

      if (shouldResetMenu) {
        resetToMenu({
          ...MENU_ROUTES.pickupOrOutpost,
          params: { restaurantSlug, ...(category ? { category } : {}) },
        });

        return;
      }

      if (shouldDismissAll && canDismiss) {
        router.dismissAll();
      }

      const navigateToMenu = () => {
        router.navigate({
          pathname: MENU_ROUTES.pickupOrOutpost.pathname,
          params: { restaurantSlug, ...(category ? { category } : {}) },
        });
      };

      navigateToMenu();
    },
    [resetToMenu, shouldDismissAll, shouldResetMenu, router],
  );

  const handleGoToDeliveryMenu = React.useCallback(
    async (addressId: string, category?: string) => {
      const canDismiss = shouldDismissAll ? router.canDismiss() : false;

      if (shouldResetMenu) {
        resetToMenu({
          ...MENU_ROUTES.delivery,
          params: { addressId, ...(category ? { category } : {}) },
        });

        return;
      }

      if (shouldDismissAll && canDismiss) {
        router.dismissAll();
      }

      const navigateToDeliveryMenu = () => {
        router.navigate({
          pathname: MENU_ROUTES.delivery.pathname,
          params: { addressId, ...(category ? { category } : {}) },
        });
      };

      navigateToDeliveryMenu();
    },
    [resetToMenu, shouldDismissAll, shouldResetMenu, router],
  );

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  return useCallback(
    ({ orderType, restaurantSlug = '', deliveryOrderDetail, category }: UseNavigateToMenuProps) => {
      if (['pickup', 'outpost'].includes(orderType)) {
        void handleGoToMenu(restaurantSlug, category);

        return;
      }

      // Block going to delivery menu when there is an in-flight delivery order.
      const hasInFlightDeliveryOrder = Boolean(inFlightDeliveryOrderId);
      const shouldBlockNavigation = shouldBlockOnInFlightDeliveryOrder && hasInFlightDeliveryOrder;

      if (shouldBlockNavigation) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const deliveryType = deliveryOrderDetail as Readonly<{
        address: { id: string };
      }>;
      const addressId = deliveryOrderDetail?.addressId ?? deliveryType?.address?.id;

      if (orderType === 'delivery' && addressId) {
        void handleGoToDeliveryMenu(addressId, category);
      }
    },
    [
      inFlightDeliveryOrderId,
      shouldBlockOnInFlightDeliveryOrder,
      handleGoToDeliveryMenu,
      handleGoToMenu,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MENU_ROUTES = {
  delivery: {
    pathname: '/delivery/[addressId]/menu',
    screenName: 'delivery/[addressId]/menu/index',
  },
  pickupOrOutpost: {
    pathname: '/[restaurantSlug]/menu',
    screenName: '[restaurantSlug]/menu/index',
  },
} as const satisfies Record<string, MenuRouteDefinition>;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  backToPath?: Href;
  isInsideModal?: boolean;
  shouldBlockOnInFlightDeliveryOrder?: boolean;
};

type MenuRoute = {
  [Key in MenuRouteKey]: Extract<Href, object> &
    (typeof MENU_ROUTES)[Key] & {
      params: RouteParams<(typeof MENU_ROUTES)[Key]['pathname']>;
    };
}[MenuRouteKey];

type MenuRouteKey = keyof typeof MENU_ROUTES;

type MenuRouteDefinition = {
  pathname: RoutePath;
  screenName: StackRouteName<RoutePath>;
};

type StackRouteName<Route extends RoutePath> = Route extends `/${infer Path}`
  ? `${Path}/index`
  : never;

export type UseNavigateToMenuProps = Readonly<{
  orderType: AddressType;
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
  deliveryAddressName?: string;
  deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  referrer?: MenuScreenReferrer;
  category?: string;
}>;

type MenuScreenReferrer = 'bag';
